import { useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import type IPressMaterial from '../../../../models/press/press-material.model';
import { TranslationService } from '../../../../services/translation.service';
import PressMaterial from '../PressMaterial';
import { PressMaterialApiModel } from '../../models/press-material.api-model';
import type PaginationResultApiModel from '../../../../services/pagination-result.api-model';
import type { IPaginationResult } from '../../../../models/pagination-result.model';
import { PressMaterialsApiModel } from '../../models/press-materials.api-model';
import Spinner from '../../../Shared/Spinner';
import { Status } from '../../../../hooks/fetch.hook';
import Alert from '../../../Shared/Alert';

async function getPressMaterial(limit: number, offset: number): Promise<IPaginationResult<IPressMaterial>> {
  const response = await fetch(`/api/pressinformation/material?limit=${limit}&offset=${offset}`);
  if (!response.ok) throw new Error(response.statusText);

  const result: PaginationResultApiModel<PressMaterialApiModel> = await response.json();
  return PressMaterialsApiModel.mapToBusinessModel(result);
}

function PressMaterialTabPanel({ active = false }: { active: boolean }) {
  const limit = 12;

  const [items, setItems] = useState<IPressMaterial[]>([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [status, setStatus] = useState({ status: Status.notperformed, updated: DateTime.now() });
  const [currentOffset, setCurrentOffset] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setStatus({ status: Status.loading, updated: DateTime.now() });
      const result = await getPressMaterial(limit, currentOffset);
      setCurrentOffset(currentOffset + result.count);
      setItems(items.concat(result.results));
      setShowLoadMore(currentOffset + result.count < result.totalCount);
      setStatus({ status: Status.success, updated: DateTime.now() });
    } catch {
      setStatus({ status: Status.failed, updated: DateTime.now() });
    }
  }, [currentOffset, items]);

  useEffect(() => {
    if (active && !items.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchData();
    }
  }, [active, fetchData, items]);

  async function handleOnMoreClicked() {
    await fetchData();
  }

  return (
    <div className="tab-panel">
      {TranslationService.get('presspage.copyrighttext') && <p className="mb-4">{TranslationService.get('presspage.copyrighttext')}</p>}
      {items && (
        <ol id="press-material-items" className="row p-0" aria-live="polite">
          {items.map((pressMaterial) => (
            <li key={pressMaterial.id} className="col-12 col-sm-6 col-md-4 mb-4 mb-sm-5">
              <PressMaterial pressMaterial={pressMaterial} />
            </li>
          ))}
        </ol>
      )}
      {showLoadMore && (
        <button className="btn btn-tertiary d-block mx-auto" aria-controls="press-material-items" onClick={handleOnMoreClicked}>
          {TranslationService.get('presspage.more')}
        </button>
      )}
      {status.status === Status.loading && <Spinner delayInMs={300} className="my-3" />}
      <Alert
        show={status.status === Status.failed}
        heading={TranslationService.get('presspage.generalerror')}
        headingTag="h3"
        closable={false}
        className={showLoadMore ? 'mt-3' : ''}
      />
    </div>
  );
}

export default PressMaterialTabPanel;